import { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import ArticleCard from "./components/ArticleCard";
import { beautyArticles, beautyCategories } from "@/mocks/beauty-info";
import { scrollToTop } from "@/components/feature/ScrollToTop";

export default function BeautyInfo() {
  const [activeCategory, setActiveCategory] = useState<string>("전체");
  const [page, setPage] = useState<number>(1);
  const pageSize = 6;

  const filteredArticles =
    activeCategory === "전체"
      ? beautyArticles
      : beautyArticles.filter((article) => article.category === activeCategory);

  const featured = beautyArticles.find((article) => article.featured);

  const listArticles = filteredArticles.filter(
    (article) => article.id !== featured?.id || activeCategory !== "전체"
  );
  const totalPages = Math.max(1, Math.ceil(listArticles.length / pageSize));
  const paginatedArticles = listArticles.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <PageHero
        eyebrow="Beauty Info"
        title="뷰티정보"
        description="건강하고 빛나는 피부를 위한 유용한 피부 관리 정보와 팁을 소개합니다."
        image="https://readdy.ai/api/search-image?query=Soft%20elegant%20beauty%20magazine%20hero%20background%20with%20skincare%20products%20botanical%20leaves%20and%20delicate%20floral%20elements%20in%20dusty%20pink%20cream%20and%20mint%20tones%20minimalist%20editorial%20aesthetic%20bright%20high%20key&width=1920&height=700&seq=bianca-beauty-hero&orientation=landscape"
      />

      <section className="w-full px-4 md:px-6 py-16 md:py-24">
        <div className="max-w-6xl mx-auto flex flex-col gap-12">
          {/* 카테고리 필터 */}
          <div className="flex items-center justify-center">
            <div className="inline-flex items-center flex-wrap gap-1 px-1 py-1 rounded-full bg-background-100/80 border border-background-200/70">
              {beautyCategories.map((category) => {
                const active = activeCategory === category;
                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => {
                      setActiveCategory(category);
                      setPage(1);
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      active
                        ? "bg-primary-500 text-white"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 추천 글 */}
          {activeCategory === "전체" && featured && (
            <Link
              to={`/beauty-info/${featured.id}`}
              onClick={scrollToTop}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden bg-white border border-background-200/70 transition-all hover:border-primary-200 cursor-pointer"
            >
              <div className="relative w-full h-64 lg:h-full overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  title={`${featured.title} - 비앙카 뷰티정보`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-5 left-5 px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-medium">
                  추천 글
                </span>
              </div>
              <div className="flex flex-col justify-center gap-4 p-8 md:p-10">
                <div className="flex items-center gap-3 text-xs text-foreground-500">
                  <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-600 font-medium">
                    {featured.category}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {featured.date}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {featured.readTime}
                  </span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors">
                  {featured.title}
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{featured.excerpt}</p>
                <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 group-hover:gap-3 transition-all">
                  자세히 보기
                  <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          )}

          {/* 글 목록 */}
          {paginatedArticles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                      scrollToTop();
                    }}
                    disabled={page === 1}
                    className="w-10 h-10 rounded-full border border-background-200 bg-white flex items-center justify-center text-foreground-700 transition-all cursor-pointer hover:border-primary-300 hover:text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-background-200 disabled:hover:text-foreground-700"
                    aria-label="이전 페이지"
                  >
                    <i className="ri-arrow-left-s-line text-xl"></i>
                  </button>
                  {Array.from({ length: totalPages }).map((_, index) => {
                    const pageNumber = index + 1;
                    const active = pageNumber === page;
                    return (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => {
                          setPage(pageNumber);
                          scrollToTop();
                        }}
                        className={`w-10 h-10 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                          active
                            ? "bg-primary-500 text-white"
                            : "border border-background-200 bg-white text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => {
                      setPage((p) => Math.min(totalPages, p + 1));
                      scrollToTop();
                    }}
                    disabled={page === totalPages}
                    className="w-10 h-10 rounded-full border border-background-200 bg-white flex items-center justify-center text-foreground-700 transition-all cursor-pointer hover:border-primary-300 hover:text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-background-200 disabled:hover:text-foreground-700"
                    aria-label="다음 페이지"
                  >
                    <i className="ri-arrow-right-s-line text-xl"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-3xl text-primary-500"></i>
              </span>
              <p className="text-sm text-foreground-500">해당 카테고리의 글이 아직 없습니다.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}